/* =============================================================
   TD MEGA MENU - behaviour for <td-mega-menu>
   Markup: snippets/td-header-mega-menu.liquid

   Progressive enhancement over <details>/<summary>: without this
   file panels still open and close on click.
   Adds: hover intent (120ms open / 200ms close, mouse only),
   one panel open at a time, Escape + outside click + focus-out
   close, click-through on an already-open summary, rail hover
   views, and panel max-height under a sticky header.
   ============================================================= */

if (!customElements.get('td-mega-menu')) {
  class TdMegaMenu extends HTMLElement {
    static OPEN_DELAY = 120;
    static CLOSE_DELAY = 200;

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('details.td-mega'));
      if (!this.items.length) return;

      this.items.forEach((details) => this.bindItem(details));

      this.onKeydown = (e) => {
        if (e.key !== 'Escape') return;
        const open = this.items.find((d) => d.open);
        if (!open) return;
        open.open = false;
        open.querySelector('summary').focus();
      };
      this.onDocClick = (e) => {
        if (!this.contains(e.target)) this.closeAll();
      };

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocClick);
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.closeAll();
        });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocClick);
    }

    bindItem(details) {
      const item = details.closest('[data-td-mega-item]');
      const summary = details.querySelector('summary');
      summary.setAttribute('aria-expanded', 'false');

      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
        if (details.open) {
          this.closeAll(details);
          this.setTop();
          this.loadBestsellers(details);
        } else {
          this.setView(details, 'all');
        }
      });

      // Hover intent - mouse only; touch and pen use tap.
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.loadBestsellers(details); // start the fetch before the open delay ends
        clearTimeout(this.closeTimer);
        clearTimeout(this.openTimer);
        const delay = this.items.some((d) => d.open) ? 0 : TdMegaMenu.OPEN_DELAY;
        this.openTimer = setTimeout(() => { details.open = true; }, delay);
      });

      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(this.openTimer);
        this.closeTimer = setTimeout(() => { details.open = false; }, TdMegaMenu.CLOSE_DELAY);
      });

      // Mouse click on a summary that hover has already opened goes
      // to the category page instead of snapping the panel shut.
      summary.addEventListener('pointerdown', (e) => { this.lastPointer = e.pointerType; });
      summary.addEventListener('click', (e) => {
        if (this.lastPointer === 'mouse' && details.open && summary.dataset.href) {
          e.preventDefault();
          window.location.href = summary.dataset.href;
        }
        this.lastPointer = null;
      });

      // Rail: hover/focus a group to swap the centre to its tiles.
      details.querySelectorAll('.td-mega__rail-link[data-group]').forEach((railLink) => {
        const show = () => this.setView(details, railLink.dataset.group);
        railLink.addEventListener('pointerenter', show);
        railLink.addEventListener('focus', show);
        if (railLink.tagName === 'BUTTON') railLink.addEventListener('click', show);
      });
    }

    setView(details, group) {
      const panel = details.querySelector('.td-mega__panel');
      if (!panel || panel.dataset.active === group) return;
      panel.dataset.active = group;
      panel.querySelectorAll('.td-mega__view[data-view]').forEach((view) => {
        view.hidden = view.dataset.view !== group;
      });
      panel.querySelectorAll('.td-mega__rail-link[data-group]').forEach((railLink) => {
        railLink.classList.toggle('is-active', railLink.dataset.group === group);
      });
    }

    // Live bestsellers: pinned picks (server-rendered) stay first, then
    // fill up to data-max from the collection's best-selling sort via
    // templates/collection.td-mega.liquid. Fetched once per panel.
    loadBestsellers(details) {
      const list = details.querySelector('[data-td-bestsellers]');
      if (!list || list.dataset.loaded) return;
      list.dataset.loaded = 'true';

      const max = parseInt(list.dataset.max, 10) || 4;
      const have = () => list.querySelectorAll('[data-product-id]');
      const wrap = list.closest('.td-mega__products');
      if (have().length >= max) return;

      fetch(list.dataset.tdBestsellers, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const tpl = document.createElement('template');
          tpl.innerHTML = html;
          const seen = new Set(Array.from(have()).map((el) => el.dataset.productId));
          tpl.content.querySelectorAll('[data-product-id]').forEach((el) => {
            if (have().length >= max || seen.has(el.dataset.productId)) return;
            seen.add(el.dataset.productId);
            list.appendChild(el);
          });
        })
        .catch(() => {})
        .finally(() => {
          if (wrap && !have().length) wrap.hidden = true;
        });
    }

    closeAll(except = null) {
      this.items.forEach((d) => {
        if (d !== except && d.open) d.open = false;
      });
    }

    // Keeps a tall panel inside the viewport under the sticky header.
    setTop() {
      const anchor = this.closest('.header-wrapper') || this;
      const bottom = Math.round(anchor.getBoundingClientRect().bottom);
      this.style.setProperty('--td-mega-top', `${Math.max(bottom, 0)}px`);
    }
  }

  customElements.define('td-mega-menu', TdMegaMenu);
}
